import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Collapse, message, Popconfirm, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { PERMISSION_OBJ_INBOUND_EMAIL, PERMISSION_READ, PERMISSION_UPDATE } from '@/constants/permissions';
import { withPermissions } from '@/hocs/permission';
import { authStore } from '@/stores';
import { showError } from '@/utils/common';
import { getTranslatedString } from '@/utils/intl';

const REFRESH_INTERVAL_MS = 60_000;
const QUARANTINE_PAGE_SIZE = 10;

const baseQuarantineColumns: ColumnsType<cyrian.api.inboundEmails.InboundEmail> = [
  {
    title: <FormattedMessage id="boardings.imports.quarantine.column.received" />,
    dataIndex: 'receivedAt',
    key: 'receivedAt',
    width: 180,
    render: (receivedAt: string) => (receivedAt ? new Date(receivedAt).toLocaleString() : ''),
  },
  {
    title: <FormattedMessage id="boardings.imports.quarantine.column.from" />,
    dataIndex: 'fromAddress',
    key: 'fromAddress',
    ellipsis: true,
  },
  {
    title: <FormattedMessage id="boardings.imports.quarantine.column.subject" />,
    dataIndex: 'subject',
    key: 'subject',
    ellipsis: true,
  },
  {
    title: <FormattedMessage id="boardings.imports.quarantine.column.reason" />,
    dataIndex: 'reason',
    key: 'reason',
    ellipsis: true,
    render: (reason: string | null) => (reason ? <Typography.Text type="warning">{reason}</Typography.Text> : null),
  },
];

/**
 * Mail-intake visibility block: "mailbox last checked" heartbeat + the
 * quarantined-senders listing. The block is gated on the `inbound_email`
 * Read permission (withPermissions renders nothing without it); the
 * dismiss action additionally needs Update — the row is kept for
 * Message-ID dedup and audit, only its disposition changes.
 */
const IntakeStatusInner = () => {
  // undefined = not fetched yet; null = poller has never reported.
  const [lastSeenAt, setLastSeenAt] = useState<string | null | undefined>(undefined);
  const [rows, setRows] = useState<cyrian.api.inboundEmails.InboundEmail[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // Background refreshes must not toast on transient network errors — the
  // next 60 s tick usually clears them on its own.
  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.inboundEmails.mailboxStatus().source;
      setLastSeenAt(res?.data?.lastSeenAt ?? null);
    } catch (err) {
      // biome-ignore lint/suspicious/noConsole: silent background poll — a toast per transient error would spam the operator
      console.warn('[boarding-imports] mailbox status fetch failed, will retry', err);
    }
  }, []);
  const fetchQuarantine = useCallback(async (targetPage: number) => {
    setLoading(true);
    try {
      const res = await api.inboundEmails.getAll({
        disposition: ['quarantined'],
        take: QUARANTINE_PAGE_SIZE,
        skip: (targetPage - 1) * QUARANTINE_PAGE_SIZE,
      }).source;
      if (res?.data) {
        setRows(res.data.items ?? []);
        setCount(res.data.count ?? 0);
      }
    } catch (err) {
      // biome-ignore lint/suspicious/noConsole: silent background poll — a toast per transient error would spam the operator
      console.warn('[boarding-imports] quarantine fetch failed, will retry', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Keep the current page in a ref so the interval refresh re-reads the page
  // the operator is on without re-arming the timer on every page change.
  const pageRef = useRef(page);
  pageRef.current = page;

  useEffect(() => {
    void fetchStatus();
    void fetchQuarantine(1);
    const timer = setInterval(() => {
      void fetchStatus();
      void fetchQuarantine(pageRef.current);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchStatus, fetchQuarantine]);

  const onPageChange = (nextPage: number) => {
    setPage(nextPage);
    void fetchQuarantine(nextPage);
  };

  const onDismiss = useCallback(
    async (id: number) => {
      try {
        await api.inboundEmails.dismiss(id).source;
        message.success(getTranslatedString('boardings.imports.quarantine.dismissed'));
        void fetchQuarantine(pageRef.current);
      } catch (err) {
        showError(err);
      }
    },
    [fetchQuarantine],
  );

  const canDismiss = authStore.hasPermissions([[PERMISSION_OBJ_INBOUND_EMAIL, PERMISSION_UPDATE]]);
  const quarantineColumns = useMemo(() => {
    if (!canDismiss) return baseQuarantineColumns;
    return [
      ...baseQuarantineColumns,
      {
        title: <FormattedMessage id="table.actions" />,
        key: 'actions',
        width: 110,
        render: (_: unknown, row: cyrian.api.inboundEmails.InboundEmail) => (
          <Popconfirm
            title={<FormattedMessage id="boardings.imports.quarantine.dismiss-confirm" />}
            onConfirm={() => onDismiss(row.id)}
          >
            <Button size="small">
              <FormattedMessage id="boardings.imports.quarantine.dismiss" />
            </Button>
          </Popconfirm>
        ),
      },
    ];
  }, [canDismiss, onDismiss]);

  const renderLastChecked = () => {
    if (lastSeenAt === undefined) return '…';
    if (lastSeenAt === null) return getTranslatedString('boardings.imports.mailbox.never');
    const diffMin = Math.max(0, Math.round((Date.now() - new Date(lastSeenAt).getTime()) / 60_000));
    if (diffMin < 60) return getTranslatedString('boardings.imports.mailbox.min-ago', { minutes: diffMin });
    return new Date(lastSeenAt).toLocaleString();
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Alert
        // Warning when the poller has never reported — the mailbox is not
        // being watched, which the operator must be able to tell apart from
        // "no SIs arriving".
        type={lastSeenAt === null ? 'warning' : 'info'}
        showIcon
        icon={<FontAwesomeIcon icon={faEnvelope} />}
        message={
          <FormattedMessage id="boardings.imports.mailbox.last-checked" values={{ when: renderLastChecked() }} />
        }
        style={{ marginBottom: 8 }}
      />
      <Collapse>
        <Collapse.Panel key="quarantine" header={getTranslatedString('boardings.imports.quarantine.title', { count })}>
          <Table
            rowKey="id"
            size="small"
            columns={quarantineColumns}
            dataSource={rows}
            loading={loading}
            pagination={{
              current: page,
              pageSize: QUARANTINE_PAGE_SIZE,
              total: count,
              onChange: onPageChange,
              hideOnSinglePage: true,
            }}
          />
        </Collapse.Panel>
      </Collapse>
    </div>
  );
};

export const IntakeStatus = withPermissions([[PERMISSION_OBJ_INBOUND_EMAIL, PERMISSION_READ]])(IntakeStatusInner);
